import React, { useState } from "react";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { usePageGuideContextOptional } from "./PageGuideContext";
import { speakPageGuideText, unlockPageGuideAudio } from "./pageGuideAudio";

const CALIBRATION_PHRASE =
  "This is PageGuide. If you can hear me, tap Yes.";

/**
 * First-use audio calibration after the user enables PageGuide.
 */
function PageGuideCalibrationDialog() {
  const ctx = usePageGuideContextOptional();
  const [playing, setPlaying] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);

  if (!ctx?.calibrationOpen) return null;

  const playTest = async () => {
    unlockPageGuideAudio();
    setPlaying(true);
    try {
      await speakPageGuideText(CALIBRATION_PHRASE);
    } finally {
      setPlaying(false);
    }
  };

  return (
    <Dialog
      open={ctx.calibrationOpen}
      onClose={() => {}}
      aria-labelledby="pageguide-calibration-title"
      data-testid="pageguide-calibration"
    >
      <DialogTitle id="pageguide-calibration-title">
        Can you hear PageGuide?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            We will play a short phrase so you can confirm your device volume
            and speakers are working. PageGuide never listens unless you tap
            Talk to guide.
          </Typography>
          {showGuidance ? (
            <Alert severity="info">
              Check your device volume, mute/ring switch (especially on
              iPhone/iPad), and whether Bluetooth or headphones are connected.
              Captions will still appear even if audio is off.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <LoadingButton
          loading={playing}
          onClick={playTest}
          data-testid="pageguide-calibration-play"
        >
          Play test
        </LoadingButton>
        <Button
          onClick={() => {
            setShowGuidance(true);
            void ctx.completeCalibration(false);
          }}
          data-testid="pageguide-calibration-nohear"
        >
          I didn&apos;t hear that
        </Button>
        <Button
          color="primary"
          onClick={() => void ctx.completeCalibration(true)}
          data-testid="pageguide-calibration-yes"
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PageGuideCalibrationDialog;
